import React, { Fragment } from 'react';
import { Box, Card, CardContent, Grid, makeStyles, Typography } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';

import { Dimensions } from 'components';
import { DimensionsProvider } from 'providers';
import { useContent, useSharePhoto, useReleases } from 'hooks';

import {
  isEmbed,
  getEmbed,
  getPhoto,
  isUrl,
  isTextOnly,
  getProviderDisplay,
} from 'features/content/helpers';
import { ExternalLink, Image, Photo } from 'features/content/widgets';

import SharingThumbnails from './Thumbnails';

const useStyles = makeStyles((theme: Theme) => ({
  break: theme.mixins.breakWord,
}));

const Preview = () => {
  const classes = useStyles();
  const content = useContent();
  const releases = useReleases();
  const { selectedPhotoIndex } = useSharePhoto();

  const hasVariations = releases.includes('shareVariations');
  const { content_object } = content;

  function renderEmbed() {
    return <Fragment>{getEmbed(content)}</Fragment>;
  }

  function renderPhoto(disableViewer: boolean) {
    const photo = getPhoto(content, selectedPhotoIndex);

    return (
      <Dimensions>
        {dimensions => (
          <DimensionsProvider value={dimensions}>
            {photo ? (
              <Photo disableViewer={disableViewer} photo={photo} />
            ) : (
              <Image content={content} />
            )}
          </DimensionsProvider>
        )}
      </Dimensions>
    );
  }

  function renderUrl() {
    const { url_metadata } = content_object;
    const providerDisplay = getProviderDisplay(content);

    return (
      <Card>
        <Box px={2} py={1}>
          <ExternalLink>
            <Typography className={classes.break} color="textSecondary">
              {providerDisplay}
            </Typography>
          </ExternalLink>
        </Box>
        <ExternalLink>{renderPhoto(true)}</ExternalLink>
        <CardContent>
          <Box py={1}>
            {hasVariations && (
              <Grid container spacing={1}>
                <SharingThumbnails images={content_object.photos} />
              </Grid>
            )}
            <ExternalLink>
              <Typography paragraph variant="h4" className={classes.break}>
                {url_metadata.title}
              </Typography>
              <Typography color="textSecondary">{url_metadata.description}</Typography>
            </ExternalLink>
          </Box>
        </CardContent>
      </Card>
    );
  }

  function renderImage() {
    return (
      <Card>
        {renderPhoto(false)}
        {hasVariations && (
          <Box px={2}>
            <Grid container spacing={1}>
              <SharingThumbnails images={content_object.photos} />
            </Grid>
          </Box>
        )}
      </Card>
    );
  }

  if (isEmbed(content)) {
    return renderEmbed();
  } else if (isTextOnly(content)) {
    return null;
  } else if (isUrl(content)) {
    return renderUrl();
  }

  return renderImage();
};

export default Preview;
